import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Button,
    Card,
    CardBody,
    Typography,
    Chip,
    Tabs,
    TabsHeader,
    TabsBody,
    Tab,
    TabPanel
} from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEdit,
    faArrowRight,
    faPrint,
    faClock,
    faUser,
    faDownload,
    faEye,
    faTimes
} from '@fortawesome/free-solid-svg-icons';
import JobCardParts from './JobCardParts';
import StatusChangeModal from './StatusChangeModal';
import JobCardDocumentPreviewDialog from './JobCardDocumentPreviewDialog';
import { fetchPrintableJobCard } from './jobCardDocumentService';
import { downloadJobCardPDF, printJobCardPDF } from './jobCardPDFGenerator';
const { rootUrl } = require('../../../config/config');

const JobCardDetails = ({ open, onClose, jobCardId, onEdit, onStatusUpdate }) => {
    const { token, user } = useAuth();
    const authToken = token || localStorage.getItem('authToken') || 'default-token';
    const generatedBy = user?.name || user?.username || localStorage.getItem('cfms-name') || localStorage.getItem('username') || 'Fleet Management';

    const [jobCard, setJobCard] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('details');
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [pendingNextStatus, setPendingNextStatus] = useState(null);
    const [showDocumentPreview, setShowDocumentPreview] = useState(false);
    const [documentBusy, setDocumentBusy] = useState('');
    const [documentError, setDocumentError] = useState('');

    const fetchJobCard = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(
                `${rootUrl}/api/job-cards/${jobCardId}`,
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );

            if (response.data && response.data.success) {
                setJobCard(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching job card:', error);
        } finally {
            setLoading(false);
        }
    }, [authToken, jobCardId]);

    useEffect(() => {
        if (open && jobCardId) {
            fetchJobCard();
        }
    }, [open, jobCardId, fetchJobCard]);

    const handleStatusUpdateClick = (newStatus) => {
        setPendingNextStatus(newStatus);
        setShowStatusModal(true);
    };

    const handleStatusUpdate = async (statusData) => {
        try {
            const response = await axios.patch(
                `${rootUrl}/api/job-cards/${jobCardId}/status`,
                {
                    status: pendingNextStatus,
                    updated_by: user?.username || localStorage.getItem('username') || 'user',
                    ...statusData
                },
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );

            if (response.data && response.data.success) {
                setJobCard(response.data.data);
                onStatusUpdate && onStatusUpdate(response.data.data);
                setShowStatusModal(false);
                setPendingNextStatus(null);
                // Auto reload
                window.location.reload();
            }
        } catch (error) {
            console.error('Error updating status:', error);
            alert(error.response?.data?.message || 'Failed to update status');
        }
    };

    const loadPrintableJobCard = async () => fetchPrintableJobCard(authToken, jobCardId);

    const handleDownloadDocument = async () => {
        try {
            setDocumentBusy('download');
            setDocumentError('');
            const payload = await loadPrintableJobCard();
            await downloadJobCardPDF(payload.jobCard, payload.companyInfo, generatedBy);
        } catch (err) {
            console.error(err);
            setDocumentError(err.response?.data?.message || err.message || 'Failed to download PDF');
        } finally {
            setDocumentBusy('');
        }
    };

    const handlePrintDocument = async () => {
        try {
            setDocumentBusy('print');
            setDocumentError('');
            const payload = await loadPrintableJobCard();
            await printJobCardPDF(payload.jobCard, payload.companyInfo, generatedBy);
        } catch (err) {
            console.error(err);
            setDocumentError(err.response?.data?.message || err.message || 'Failed to print PDF');
        } finally {
            setDocumentBusy('');
        }
    };

    const calculateTimeOpen = () => {
        if (!jobCard || !jobCard.opened_date) return null;
        const opened = new Date(jobCard.opened_date);
        const now = new Date();
        const diffMs = now - opened;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        
        if (diffDays > 0) {
            return `${diffDays} day${diffDays > 1 ? 's' : ''}, ${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
        } else if (diffHours > 0) {
            return `${diffHours} hour${diffHours > 1 ? 's' : ''}, ${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''}`;
        } else {
            return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''}`;
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            'open': 'blue',
            'in_progress': 'yellow',
            'completed': 'green',
            'invoiced': 'purple',
            'closed': 'gray'
        };
        return colors[status] || 'gray';
    };

    const getNextStatus = (currentStatus) => {
        const transitions = {
            'open': 'in_progress',
            'in_progress': 'completed',
            'completed': 'invoiced',
            'invoiced': 'closed'
        };
        return transitions[currentStatus];
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString();
    };

    const formatCurrency = (amount) => {
        if (!amount) return '0.00';
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES'
        }).format(amount);
    };

    if (!open) return null;

    if (loading) {
        return (
            <Dialog open={open} handler={onClose} size="xl">
                <DialogBody className="text-center py-8">
                    <Typography>Loading job card details...</Typography>
                </DialogBody>
            </Dialog>
        );
    }

    if (!jobCard) {
        return (
            <Dialog open={open} handler={onClose} size="xl">
                <DialogBody className="text-center py-8">
                    <Typography color="red">Job card not found</Typography>
                </DialogBody>
            </Dialog>
        );
    }

    const computedNextStatus = getNextStatus(jobCard.status);

    return (
        <Dialog open={open} handler={onClose} size="xl">
            <DialogHeader className="flex justify-between items-center">
                <Typography variant="h5">Job Card: {jobCard.job_card_no}</Typography>
                <div className="flex gap-2">
                    <Button size="sm" variant="outlined" className="flex items-center gap-2 bg-white text-slate-700 border-slate-300" onClick={() => setShowDocumentPreview(true)}>
                        <FontAwesomeIcon icon={faEye} />
                        Preview
                    </Button>
                    <Button size="sm" variant="outlined" className="flex items-center gap-2 bg-white text-slate-700 border-slate-300" onClick={handleDownloadDocument} disabled={documentBusy === 'download'}>
                        <FontAwesomeIcon icon={faDownload} />
                        {documentBusy === 'download' ? 'Downloading...' : 'Download PDF'}
                    </Button>
                    <Button size="sm" variant="outlined" className="flex items-center gap-2 bg-white text-slate-700 border-slate-300" onClick={handlePrintDocument} disabled={documentBusy === 'print'}>
                        <FontAwesomeIcon icon={faPrint} />
                        {documentBusy === 'print' ? 'Printing...' : 'Print'}
                    </Button>
                    {onEdit && (
                        <Button size="sm" color="blue" onClick={() => onEdit(jobCard)}>
                            <FontAwesomeIcon icon={faEdit} className="mr-2" />
                            Edit
                        </Button>
                    )}
                    {computedNextStatus && (
                        <Button
                            size="sm"
                            color="green"
                            onClick={() => handleStatusUpdateClick(computedNextStatus)}
                        >
                            <FontAwesomeIcon icon={faArrowRight} className="mr-2" />
                            Move to {computedNextStatus.replace('_', ' ').toUpperCase()}
                        </Button>
                    )}
                </div>
            </DialogHeader>
            {documentError && (
                <div className="mx-6 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {documentError}
                </div>
            )}
            <JobCardDocumentPreviewDialog
                open={showDocumentPreview}
                onClose={() => setShowDocumentPreview(false)}
                jobCardId={jobCardId}
            />
            <DialogBody>
                <Tabs value={activeTab} onChange={setActiveTab}>
                    <TabsHeader>
                        <Tab value="details">Details</Tab>
                        <Tab value="parts">Parts</Tab>
                        <Tab value="financials">Financials</Tab>
                    </TabsHeader>
                    <TabsBody>
                        <TabPanel value="details">
                            <div className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <Card>
                                        <CardBody>
                                            <Typography variant="small" color="gray">Plate Number</Typography>
                                            <Typography variant="h6">{jobCard.plate_no}</Typography>
                                        </CardBody>
                                    </Card>
                                    <Card>
                                        <CardBody>
                                            <Typography variant="small" color="gray">Status</Typography>
                                            <Chip
                                                value={jobCard.status?.replace('_', ' ').toUpperCase()}
                                                color={getStatusColor(jobCard.status)}
                                                className="mt-1"
                                            />
                                        </CardBody>
                                    </Card>
                                    <Card>
                                        <CardBody>
                                            <Typography variant="small" color="gray">Service Location</Typography>
                                            <Typography variant="h6">
                                                {jobCard.service_location === 'in_house' ? 'In-House' : 'External'}
                                            </Typography>
                                        </CardBody>
                                    </Card>
                                    <Card>
                                        <CardBody>
                                            <Typography variant="small" color="gray">Opened Date</Typography>
                                            <Typography variant="h6">{formatDate(jobCard.opened_date)}</Typography>
                                        </CardBody>
                                    </Card>
                                    <Card>
                                        <CardBody>
                                            <Typography variant="small" color="gray" className="flex items-center gap-1">
                                                <FontAwesomeIcon icon={faClock} size="sm" />
                                                Time Open
                                            </Typography>
                                            <Typography variant="h6" className="mt-1">
                                                {calculateTimeOpen() || 'N/A'}
                                            </Typography>
                                        </CardBody>
                                    </Card>
                                    <Card>
                                        <CardBody>
                                            <Typography variant="small" color="gray" className="flex items-center gap-1">
                                                <FontAwesomeIcon icon={faUser} size="sm" />
                                                Opened By
                                            </Typography>
                                            <Typography variant="h6" className="mt-1">
                                                {jobCard.input_by || 'N/A'}
                                            </Typography>
                                        </CardBody>
                                    </Card>
                                    {jobCard.service_location === 'external' && (
                                        <>
                                            <Card>
                                                <CardBody>
                                                    <Typography variant="small" color="gray">Service Center</Typography>
                                                    <Typography variant="h6">{jobCard.service_center_name || '-'}</Typography>
                                                </CardBody>
                                            </Card>
                                            <Card>
                                                <CardBody>
                                                    <Typography variant="small" color="gray">Contact</Typography>
                                                    <Typography variant="h6">{jobCard.service_center_contact || '-'}</Typography>
                                                </CardBody>
                                            </Card>
                                        </>
                                    )}
                                    {jobCard.mileage_at_service && (
                                        <Card>
                                            <CardBody>
                                                <Typography variant="small" color="gray">Mileage at Service</Typography>
                                                <Typography variant="h6">{jobCard.mileage_at_service.toLocaleString()} km</Typography>
                                            </CardBody>
                                        </Card>
                                    )}
                                </div>

                                {jobCard.diagnosis && (
                                    <Card>
                                        <CardBody>
                                            <Typography variant="small" color="gray">Diagnosis</Typography>
                                            <Typography variant="paragraph" className="mt-2">{jobCard.diagnosis}</Typography>
                                        </CardBody>
                                    </Card>
                                )}

                                {jobCard.work_performed && (
                                    <Card>
                                        <CardBody>
                                            <Typography variant="small" color="gray">Work Performed</Typography>
                                            <Typography variant="paragraph" className="mt-2">{jobCard.work_performed}</Typography>
                                        </CardBody>
                                    </Card>
                                )}

                                {jobCard.mechanic_name && (
                                    <Card>
                                        <CardBody>
                                            <Typography variant="small" color="gray">Mechanic</Typography>
                                            <Typography variant="h6">{jobCard.mechanic_name}</Typography>
                                        </CardBody>
                                    </Card>
                                )}

                                {jobCard.status === 'invoiced' && (
                                    <>
                                        {jobCard.invoice_reference && (
                                            <Card>
                                                <CardBody>
                                                    <Typography variant="small" color="gray">Invoice Reference</Typography>
                                                    <Typography variant="h6">{jobCard.invoice_reference}</Typography>
                                                </CardBody>
                                            </Card>
                                        )}
                                        {jobCard.invoice_skipped && (
                                            <Card>
                                                <CardBody>
                                                    <Typography variant="small" color="gray">Invoicing Status</Typography>
                                                    <Chip value="Skipped" color="orange" className="mt-1" />
                                                    {jobCard.invoice_skip_notes && (
                                                        <Typography variant="small" color="gray" className="mt-2">
                                                            {jobCard.invoice_skip_notes}
                                                        </Typography>
                                                    )}
                                                </CardBody>
                                            </Card>
                                        )}
                                    </>
                                )}

                                {jobCard.status === 'closed' && jobCard.drive_link && (
                                    <Card>
                                        <CardBody>
                                            <Typography variant="small" color="gray">Drive Link</Typography>
                                            <a 
                                                href={jobCard.drive_link} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:underline break-words"
                                            >
                                                {jobCard.drive_link}
                                            </a>
                                        </CardBody>
                                    </Card>
                                )}

                                {jobCard.status_change_notes && (
                                    <Card>
                                        <CardBody>
                                            <Typography variant="small" color="gray">Status Change Notes</Typography>
                                            <Typography variant="paragraph" className="mt-2">
                                                {jobCard.status_change_notes}
                                            </Typography>
                                        </CardBody>
                                    </Card>
                                )}
                            </div>
                        </TabPanel>

                        <TabPanel value="parts">
                            <JobCardParts
                                jobCardId={jobCardId}
                                serviceLocation={jobCard.service_location}
                                onUpdate={fetchJobCard}
                            />
                        </TabPanel>

                        <TabPanel value="financials">
                            <div className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <Card>
                                        <CardBody>
                                            <Typography variant="small" color="gray">Parts Cost</Typography>
                                            <Typography variant="h6" className="text-blue-600">
                                                {formatCurrency(jobCard.parts_cost)}
                                            </Typography>
                                        </CardBody>
                                    </Card>
                                    <Card>
                                        <CardBody>
                                            <Typography variant="small" color="gray">Labor Cost</Typography>
                                            <Typography variant="h6" className="text-green-600">
                                                {formatCurrency(jobCard.labor_cost)}
                                            </Typography>
                                        </CardBody>
                                    </Card>
                                    {jobCard.external_mechanic_cost > 0 && (
                                        <Card>
                                            <CardBody>
                                                <Typography variant="small" color="gray">External Mechanic Cost</Typography>
                                                <Typography variant="h6" className="text-orange-600">
                                                    {formatCurrency(jobCard.external_mechanic_cost)}
                                                </Typography>
                                            </CardBody>
                                        </Card>
                                    )}
                                    <Card>
                                        <CardBody>
                                            <Typography variant="small" color="gray">Total Cost</Typography>
                                            <Typography variant="h5" className="text-red-600 font-bold">
                                                {formatCurrency(jobCard.total_cost)}
                                            </Typography>
                                        </CardBody>
                                    </Card>
                                    <Card>
                                        <CardBody>
                                            <Typography variant="small" color="gray">Paid By</Typography>
                                            <Typography variant="h6">{jobCard.paid_by || '-'}</Typography>
                                        </CardBody>
                                    </Card>
                                    <Card>
                                        <CardBody>
                                            <Typography variant="small" color="gray">Payment Status</Typography>
                                            <Chip
                                                value={jobCard.payment_status || 'PENDING'}
                                                color={jobCard.payment_status === 'PAID' ? 'green' : 'yellow'}
                                                className="mt-1"
                                            />
                                        </CardBody>
                                    </Card>
                                </div>
                            </div>
                        </TabPanel>
                    </TabsBody>
                </Tabs>
            </DialogBody>
            <DialogFooter>
                <Button variant="text" color="red" onClick={onClose}>
                    <FontAwesomeIcon icon={faTimes} className="mr-2" />
                    Close
                </Button>
            </DialogFooter>

            {/* Status Change Modal */}
            <StatusChangeModal
                open={showStatusModal}
                onClose={() => {
                    setShowStatusModal(false);
                    setPendingNextStatus(null);
                }}
                currentStatus={jobCard?.status}
                newStatus={pendingNextStatus}
                onConfirm={handleStatusUpdate}
                user={user}
            />
        </Dialog>
    );
};

export default JobCardDetails;

